import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { DynamicHead } from "@/components/seo/DynamicHead";
import { AnimatePresence } from "framer-motion";
import PageTransition from "@/components/layout/PageTransition";
import CookieConsent from "@/components/CookieConsent";
import ConnectionErrorBoundary from "@/components/ConnectionErrorBoundary";

// Eager load the landing page for best LCP
import Index from "./pages/Index";
import AdminRoute from "@/components/auth/AdminRoute";

// Lazy load all other pages for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const DashboardNew = lazy(() => import("./pages/DashboardNew"));
const OfferDetail = lazy(() => import("./pages/OfferDetail"));
const UploadContract = lazy(() => import("./pages/UploadContract"));
const ContractsList = lazy(() => import("./pages/ContractsList"));
const TakeHomeCalculator = lazy(() => import("./pages/TakeHomeCalculator"));
const WhatIfCalculator = lazy(() => import("./pages/WhatIfCalculator"));
const SalaryBenchmarking = lazy(() => import("./pages/SalaryBenchmarking"));
const ClauseLibrary = lazy(() => import("./pages/ClauseLibrary"));
const ContractEducation = lazy(() => import("./pages/ContractEducation"));
const CounterProposal = lazy(() => import("./pages/CounterProposal"));
const ExpertReview = lazy(() => import("./pages/ExpertReview"));
const ContractComparison = lazy(() => import("./pages/ContractComparison"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Privacy = lazy(() => import("./pages/Privacy"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Faqs = lazy(() => import("./pages/Faqs"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Settings = lazy(() => import("./pages/Settings"));
const LanguageSupport = lazy(() => import("./pages/LanguageSupport"));
const EndorsementTracker = lazy(() => import("./pages/EndorsementTracker"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
const UploadEndorsement = lazy(() => import("./pages/UploadEndorsement"));
const EndorsementDetail = lazy(() => import("./pages/EndorsementDetail"));

const ConflictChecker = lazy(() => import("./pages/ConflictChecker"));
const DeliverableCalendar = lazy(() => import("./pages/DeliverableCalendar"));
const BrandDeals = lazy(() => import("./pages/BrandDeals"));
const CostOfLifeSimulator = lazy(() => import("./pages/CostOfLifeSimulator"));
const NegotiationCoach = lazy(() => import("./pages/NegotiationCoach"));
const ContractTimeline = lazy(() => import("./pages/ContractTimeline"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const CareerDashboard = lazy(() => import("./pages/CareerDashboard"));
const SharedProposal = lazy(() => import("./pages/SharedProposal"));
const TeamDashboard = lazy(() => import("./pages/TeamDashboard"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ContractValueTracker = lazy(() => import("./pages/ContractValueTracker"));
const QADashboard = lazy(() => import("./pages/QADashboard"));
const QAAgent = lazy(() => import("./pages/QAAgent"));
const ProjectReport = lazy(() => import("./pages/ProjectReport"));
const AgentMarketplace = lazy(() => import("./pages/AgentMarketplace"));
const Monitoring = lazy(() => import("./pages/Monitoring"));
const TransferAlerts = lazy(() => import("./pages/TransferAlerts"));
const Refund = lazy(() => import("./pages/Refund"));
const RefundRequest = lazy(() => import("./pages/RefundRequest"));
const FindAgents = lazy(() => import("./pages/FindAgents"));
const Referrals = lazy(() => import("./pages/Referrals"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Accessible loading fallback with ARIA
const PageLoader = () => (
  <div
    className="min-h-screen flex items-center justify-center bg-background"
    role="status"
    aria-label="Loading page"
  >
    <div className="flex flex-col items-center gap-3">
      <div
        className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"
        aria-hidden="true"
      />
      <span className="sr-only">Loading...</span>
    </div>
  </div>
);

// Animated Routes component with location-based transitions and scroll-to-top
const AnimatedRoutes = () => {
  const location = useLocation();
  useScrollToTop();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Index /></PageTransition>} />
        <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
        <Route path="/onboarding" element={<PageTransition><Onboarding /></PageTransition>} />
        <Route path="/verify-email" element={<PageTransition><VerifyEmail /></PageTransition>} />
        <Route path="/dashboard" element={<PageTransition><DashboardNew /></PageTransition>} />
        <Route path="/offer/:id" element={<PageTransition><OfferDetail /></PageTransition>} />
        <Route path="/upload" element={<PageTransition><UploadContract /></PageTransition>} />
        <Route path="/contracts" element={<PageTransition><ContractsList /></PageTransition>} />
        <Route path="/calculator" element={<PageTransition><TakeHomeCalculator /></PageTransition>} />
        <Route path="/what-if" element={<PageTransition><WhatIfCalculator /></PageTransition>} />
        <Route path="/benchmarking" element={<PageTransition><SalaryBenchmarking /></PageTransition>} />
        <Route path="/clauses" element={<PageTransition><ClauseLibrary /></PageTransition>} />
        <Route path="/education" element={<PageTransition><ContractEducation /></PageTransition>} />
        <Route path="/counter/:id" element={<PageTransition><CounterProposal /></PageTransition>} />
        <Route path="/expert-review" element={<PageTransition><ExpertReview /></PageTransition>} />
        <Route path="/compare" element={<PageTransition><ContractComparison /></PageTransition>} />
        <Route path="/admin" element={<AdminRoute><PageTransition><AdminDashboard /></PageTransition></AdminRoute>} />
        <Route path="/privacy" element={<PageTransition><Privacy /></PageTransition>} />
        <Route path="/help" element={<PageTransition><HelpCenter /></PageTransition>} />
        <Route path="/faqs" element={<PageTransition><Faqs /></PageTransition>} />
        <Route path="/disclaimer" element={<PageTransition><Disclaimer /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
        <Route path="/about" element={<PageTransition><About /></PageTransition>} />
        <Route path="/settings" element={<PageTransition><Settings /></PageTransition>} />
        <Route path="/languages" element={<PageTransition><LanguageSupport /></PageTransition>} />
        <Route path="/endorsements" element={<PageTransition><EndorsementTracker /></PageTransition>} />
        <Route path="/analytics" element={<PageTransition><Analytics /></PageTransition>} />
        <Route path="/terms" element={<PageTransition><Terms /></PageTransition>} />
        <Route path="/upload-endorsement" element={<PageTransition><UploadEndorsement /></PageTransition>} />
        <Route path="/endorsement/:id" element={<PageTransition><EndorsementDetail /></PageTransition>} />
        <Route path="/conflict-checker" element={<PageTransition><ConflictChecker /></PageTransition>} />
        <Route path="/calendar" element={<PageTransition><DeliverableCalendar /></PageTransition>} />
        <Route path="/brand-deals" element={<PageTransition><BrandDeals /></PageTransition>} />
        <Route path="/cost-simulator" element={<PageTransition><CostOfLifeSimulator /></PageTransition>} />
        <Route path="/negotiation-coach" element={<PageTransition><NegotiationCoach /></PageTransition>} />
        <Route path="/timeline" element={<PageTransition><ContractTimeline /></PageTransition>} />
        <Route path="/notifications" element={<PageTransition><Notifications /></PageTransition>} />
        <Route path="/deliverable-calendar" element={<PageTransition><DeliverableCalendar /></PageTransition>} />
        <Route path="/contract-timeline" element={<PageTransition><ContractTimeline /></PageTransition>} />
        <Route path="/career" element={<PageTransition><CareerDashboard /></PageTransition>} />
        <Route path="/shared/:token" element={<PageTransition><SharedProposal /></PageTransition>} />
        <Route path="/team" element={<PageTransition><TeamDashboard /></PageTransition>} />
        <Route path="/pricing" element={<PageTransition><Pricing /></PageTransition>} />
        <Route path="/portfolio" element={<PageTransition><ContractValueTracker /></PageTransition>} />
        <Route path="/qa" element={<AdminRoute><PageTransition><QADashboard /></PageTransition></AdminRoute>} />
        <Route path="/qa-agent" element={<AdminRoute><PageTransition><QAAgent /></PageTransition></AdminRoute>} />
        <Route path="/project-report" element={<AdminRoute><PageTransition><ProjectReport /></PageTransition></AdminRoute>} />
        <Route path="/agents" element={<PageTransition><AgentMarketplace /></PageTransition>} />
        <Route path="/find-agents" element={<PageTransition><FindAgents /></PageTransition>} />
        <Route path="/referrals" element={<PageTransition><Referrals /></PageTransition>} />
        <Route path="/monitoring" element={<AdminRoute><PageTransition><Monitoring /></PageTransition></AdminRoute>} />
        <Route path="/transfer-alerts" element={<PageTransition><TransferAlerts /></PageTransition>} />
        <Route path="/refund" element={<PageTransition><Refund /></PageTransition>} />
        <Route path="/refund-request" element={<PageTransition><RefundRequest /></PageTransition>} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => (
  <ConnectionErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <DynamicHead />
          <Suspense fallback={<PageLoader />}>
            <AnimatedRoutes />
          </Suspense>
          <CookieConsent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ConnectionErrorBoundary>
);

export default App;
